import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";
import Loader from "../../../components/common/Loader";
import { ValidationError } from "../../../components/common/ErrorBanner";
import { ValidationErrorDisplay } from "../../../components/common/FormFields";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  isAdminWithEditPrivileges,
  getAllowedAreaCodes,
  getAllowedCenterCodes,
  getAllowedLocationCodes,
} from "../../../utils/editPrivilegeCheck";
import {
  CostCenter,
  createUBTTeam,
  fetchShiftOptions,
  fetchUBTTeam,
  updateCostCenterAPI,
  resetTeamProfile,
  setCreateTeam,
  setEditableTeam,
  setEditingTeam,
  fetchAreaOptions,
  fetchCenterOptions,
  fetchLocationOptions,
  fetchAddEditCostCenterOptions,
  defaultOption,
  resetCenterOptions,
  resetLocationOptions,
  resetCostCenterOptions,
} from "../../../store/slices/costCenterSlice";
import { convertDateToYYYYMMDD, formatCostCenter } from "../../../utils/date";
// Banner is provided by parent AddUBTProfile; child will call `setBanner` prop

// Constants for error messages
const ERROR_MESSAGES = {
  entity: "Entity is required.",
  area: "Area is required.",
  center: "Center is required.",
  location: "Location is required.",
  costCenter: "Cost Center is required.",
  shift: "Shift is required.",
} as const;

interface ManageCostCenterProps {
  costCenter: CostCenter | null;
  onCancel: () => void;
  onCloseKeepBanner?: () => void;
  onUpdate: (updated: CostCenter, didUpdate: boolean) => void;
  onSave: (addNewCostCenter: CostCenter) => Promise<boolean>;
  onSaveAndCreateNew: (addNewCostCenter: CostCenter) => Promise<boolean>;
  isAddCostCenter: boolean;
  isUpdateCostCenter: boolean;
  costCenterCount: number;
  setBanner?: (b: { heading: string; messageInfo: string; variant?: "Error" | "Success" | "Informational" | "Alert" } | null) => void;
  onValidationErrors?: (errors: ValidationError[] | null) => void;
  validationErrors?: ValidationError[] | null;
  /** Region code resolved from the team's cost centers (used to scope all dropdown API calls). */
  resolvedRegionCode?: string;
  /** Region description resolved from the team's cost centers (displayed in the disabled region input). */
  resolvedRegionDescription?: string;
}

/**
 * Renders the add/update cost center form and manages dependent dropdown reloads.
 * @param props - Component props for add/update modes, callbacks, and validation state.
 * @returns The cost center form component.
 */
const ManageCostCenter: React.FC<ManageCostCenterProps> = ({ costCenter, onCancel, onCloseKeepBanner, onSave: _onSave, isAddCostCenter, isUpdateCostCenter, onUpdate, onSaveAndCreateNew: _onSaveAndCreateNew, costCenterCount, setBanner, onValidationErrors, validationErrors: parentValidationErrors, resolvedRegionCode, resolvedRegionDescription }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Get profile from Redux (single source of truth for region)
  const profile = useAppSelector((state) => state.profile?.profile);

  // Get team info values from Redux
  const {
    ubtName,
    dateTeamEstablished,
    teamRating,
    serviceType,
    usingHuddles,
    defaultMembership,
    shiftOptions: apiShiftOptions,
    loadingShiftOptions,
    loading: loadingCostCenter,
    createTeam: isCreatingTeamMode,
    teamId: existingTeamId,
      entityOptions,
    areaOptions,
    centerOptions,
    locationOptions,
    costCenterOptions,
    loadingDropdowns
  } = useAppSelector((state) => state.costCenter);

  // Profile privileges for filtering dropdown options
  const editDataPrivileges = profile && isAdminWithEditPrivileges(profile)
    ? profile.editDataPrivileges
    : [];



  // Format cost center option labels to remove hyphen using helper
  // Use costCenterOptions directly to preserve hyphens in dropdown
  const formattedCostCenterOptions = costCenterOptions;

  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = React.useRef(true);
  
  // Track whether the user manually changed a dropdown so remapping doesn't override
  const regionUserChangedRef = React.useRef(false);
  const entityUserChangedRef = React.useRef(false);
  const areaUserChangedRef = React.useRef(false);
  const centerUserChangedRef = React.useRef(false);
  const locationUserChangedRef = React.useRef(false);
  const costCenterUserChangedRef = React.useRef(false);
  const shiftUserChangedRef = React.useRef(false);
  
  // Refs for scrolling to specific error fields
  const containerRef = React.useRef<HTMLDivElement>(null);
  const entityRef = React.useRef<HTMLDivElement>(null);
  const areaRef = React.useRef<HTMLDivElement>(null);
  const centerRef = React.useRef<HTMLDivElement>(null);
  const locationRef = React.useRef<HTMLDivElement>(null);
  const costCenterRef = React.useRef<HTMLDivElement>(null);
  const shiftRef = React.useRef<HTMLDivElement>(null);

  // Prefer the region from the team's existing cost centers (resolvedRegionCode),
  // falling back to the logged-in user's profile region. This ensures dropdowns always
  // load options for the team's actual region, not the user's home region.
  const [regionId, setRegionId] = useState(resolvedRegionCode ?? profile?.primaryRegion ?? "02");

  const [glString, setGlString] = useState("GLString-455");
  const [noOfEmployees, setNoOfEmployees] = useState("");
  const hasInitializedRef = React.useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync regionId in add mode whenever the resolved region or profile changes.
  // resolvedRegionCode (derived from the team's cost centers) takes priority over the
  // user's profile region so that dropdown API calls target the correct region.
  // Skip sync when the user has already manually selected a region via the dropdown.
  useEffect(() => {
    if (isUpdateCostCenter) return;
    if (regionUserChangedRef.current) return;
    if (resolvedRegionCode) {
      setRegionId(resolvedRegionCode);
    } else if (profile?.primaryRegion) {
      setRegionId(profile.primaryRegion);
    }
  }, [resolvedRegionCode, profile, isUpdateCostCenter]);

  const [selectedEntity, setSelectedEntity] = useState<DropdownOption>(defaultOption);
  const [selectedArea, setSelectedArea] = useState<DropdownOption>(defaultOption);
  const [selectedCenter, setSelectedCenter] = useState<DropdownOption>(defaultOption);
  const [selectedLocation, setSelectedLocation] = useState<DropdownOption>(defaultOption);
  const [selectedCostCenter, setSelectedCostCenter] = useState<DropdownOption>(defaultOption);
  const [selectedShift, setSelectedShift] = useState<DropdownOption>(defaultOption);
  // per-field inline validation messages
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Filter dropdown options based on editDataPrivileges.
  // When editDataPrivileges is empty (profile not loaded or non-admin), skip filtering.
  // null from getAllowed* means "all allowed" — pass original options unchanged.
  const filteredAreaOptions = useMemo(() => {
    if (editDataPrivileges.length === 0) return areaOptions;
    const allowed = getAllowedAreaCodes(editDataPrivileges, regionId);
    if (allowed === null) return areaOptions;
    return areaOptions.filter((o) => o.value === "Select" || allowed.has(o.value));
  }, [editDataPrivileges, regionId, areaOptions]);

  const filteredCenterOptions = useMemo(() => {
    if (editDataPrivileges.length === 0) return centerOptions;
    const allowed = getAllowedCenterCodes(editDataPrivileges, regionId, selectedArea.value);
    if (allowed === null) return centerOptions;
    return centerOptions.filter((o) => o.value === "Select" || allowed.has(o.value));
  }, [editDataPrivileges, regionId, selectedArea.value, centerOptions]);

  const filteredLocationOptions = useMemo(() => {
    if (editDataPrivileges.length === 0) return locationOptions;
    const allowed = getAllowedLocationCodes(editDataPrivileges, regionId, selectedArea.value, selectedCenter.value);
    if (allowed === null) return locationOptions;
    return locationOptions.filter((o) => o.value === "Select" || allowed.has(o.value));
  }, [editDataPrivileges, regionId, selectedArea.value, selectedCenter.value, locationOptions]);

  // Fetch Entity and Area options with regionId
  // Note: fetchAreaOptions returns both entityOptions and areaOptions in a single API call
  useEffect(() => {
    if (regionId && regionId !== "" && regionId !== "Select") {
      dispatch(fetchAreaOptions({ regionCode: regionId, sortBy: "DEPTID" }));
    }
    hasInitializedRef.current = false;
  }, [regionId, dispatch]);

  // Transform shift options from API
  const shiftOptions = React.useMemo(() => {
    // Handle both direct array and nested data property
    const shiftData = Array.isArray(apiShiftOptions)
      ? apiShiftOptions
      : (apiShiftOptions as { data?: unknown[] })?.data ?? [];

    if (shiftData.length === 0) {
      return [{ id: 0, label: "Select", value: "Select" }];
    }

    const options = shiftData.map((option: string | { label?: string; value?: string }, index: number) => {
      // If option is an object with label and value
      if (typeof option === 'object' && option.label && option.value) {
        return {
          id: index + 1,
          label: option.label,
          value: option.value,
        };
      }
      // If option is a string, use it for both label and value
      if (typeof option === 'string') {
        return {
          id: index + 1,
          label: option,
          value: option,
        };
      }
      return {
        id: index + 1,
        label: '',
        value: '',
      };
    });

    return [{ id: 0, label: "Select", value: "Select" }, ...options];
  }, [apiShiftOptions]);

  // Fetch shift options on component mount
  useEffect(() => {
    dispatch(fetchShiftOptions());
  }, [dispatch]);

  // Consolidated effect to sync all dropdown selections when options reload
  // Keeps currently selected option by matching value when options arrays change
  useEffect(() => {
    const syncOptions = [
      { options: entityOptions, selected: selectedEntity, setter: setSelectedEntity, ref: entityUserChangedRef },
      { options: areaOptions, selected: selectedArea, setter: setSelectedArea, ref: areaUserChangedRef },
      { options: centerOptions, selected: selectedCenter, setter: setSelectedCenter, ref: centerUserChangedRef },
      { options: locationOptions, selected: selectedLocation, setter: setSelectedLocation, ref: locationUserChangedRef },
      { options: formattedCostCenterOptions, selected: selectedCostCenter, setter: setSelectedCostCenter, ref: costCenterUserChangedRef },
      { options: shiftOptions, selected: selectedShift, setter: setSelectedShift, ref: shiftUserChangedRef },
    ];

    syncOptions.forEach(({ options, selected, setter, ref }) => {
      if (!options || options.length === 0 || ref.current) return;
      if (selected?.value && selected.value !== "Select") {
        const match = options.find(o => o.value === selected.value || o.label === selected.label);
        if (match && match !== selected) setter(match);
      }
    });
  }, [entityOptions, areaOptions, centerOptions, locationOptions, formattedCostCenterOptions, shiftOptions, selectedEntity, selectedArea, selectedCenter, selectedLocation, selectedCostCenter, selectedShift]);

  // Set "All" as default shift when options are loaded (add mode only)
  useEffect(() => {
    if (!loadingShiftOptions && shiftOptions.length > 0 && isAddCostCenter) {
      const allOption = shiftOptions.find(opt => opt.value === "All");
      if (allOption && selectedShift.value === "Select") {
        setSelectedShift(allOption);
      }
    }
  }, [shiftOptions, loadingShiftOptions, isAddCostCenter]);

  // Helper functions for cost center initialization
  const setInitialsWithCostCenter = React.useCallback(() => {
    // Use regionCode instead of regionId since regionId may be 0
    setRegionId(costCenter?.regionCode ?? costCenter?.regionId?.toString() ?? "");
    setGlString(costCenter?.glString ?? "");
    setNoOfEmployees(costCenter?.noOfEmployees ?? "");
  }, [costCenter]);

  const setInitialValuesWithCostCenter = React.useCallback(() => {
    if (!costCenter) return;

    setInitialsWithCostCenter();

    // Parse formatted strings: "code-description"
    // Entity: "06-TPM" -> code: "06", description: "TPM"
    const entityParts = costCenter.entity?.split('-') ?? [];
    const entityCode = entityParts[0] ?? costCenter.entityCode ?? '';
    const entityDescription = entityParts.slice(1).join('-') ?? costCenter.entity ?? '';

    // Location: "20203-San Jose Call Center" -> code: "20203", description: "San Jose Call Center"
    const locationParts = costCenter.location?.split('-') ?? [];
    const locationCode = locationParts[0] ?? costCenter.locationCode ?? '';
    const locationDescription = locationParts.slice(1).join('-') ?? costCenter.location ?? '';

    // Cost Center: "4921-Med Ofc Adm-Gen Central Svcs" -> code: "4921", description: "Med Ofc Adm-Gen Central Svcs"
    const costCenterParts = costCenter.costCenter?.split('-') ?? [];
    const costCenterCode = costCenterParts[0] ?? '';
    const costCenterDescription = costCenterParts.slice(1).join('-') ?? costCenter.costCenter ?? '';

    setSelectedEntity({
      id: costCenter.entityId ?? 0,
      label: entityDescription,
      value: entityCode
    });
    setSelectedArea({
      id: costCenter.areaId ?? 0,
      label: costCenter.area ?? '',
      value: costCenter.areaCode ?? ''
    });
    setSelectedCenter({
      id: costCenter.centerId ?? 0,
      label: costCenter.center ?? '',
      value: costCenter.centerCode ?? ''
    });
    setSelectedLocation({
      id: costCenter.locationId ?? 0,
      label: locationDescription,
      value: locationCode
    });
    setSelectedCostCenter({
      id: costCenter.costCenterId ?? 0,
      label: costCenterDescription,
      value: costCenterCode
    });
    setSelectedShift({
      id: costCenter.shiftId ?? 0,
      label: costCenter.shift ?? 'All',
      value: costCenter.shift ?? 'All'
    });
  }, [costCenter, setInitialsWithCostCenter]);

  // Initialize for update mode
  const initializeForUpdate = React.useCallback(() => {
    if (!costCenter) return;

    // Use codes from the cost center data instead of IDs (which may be 0)
    const areaCode = costCenter.areaCode ?? "";
    const centerCode = costCenter.centerCode ?? "";
    const locationCode = costCenter.locationCode ?? "";

    if (regionId && areaCode) {
      dispatch(fetchCenterOptions({ regionCode: regionId, areaCode, sortBy: "DEPTID" }));
    }
    if (regionId && areaCode && centerCode) {
      dispatch(fetchLocationOptions({ regionCode: regionId, areaCode, centerCode, sortBy: "DEPTID" }));
    }
    if (regionId && areaCode && centerCode && locationCode) {
      dispatch(
        fetchAddEditCostCenterOptions({
          regionCd: regionId,
          areaCd: areaCode,
          centerCd: centerCode,
          locationCd: locationCode,
          entityCd: costCenter?.entityCode ?? "",
        }),
      );
    }

    // Set initial values
    setInitialValuesWithCostCenter();
  }, [costCenter, regionId, dispatch, setInitialValuesWithCostCenter]);

  // Initialize for add mode
  const initializeForAdd = React.useCallback(() => {
    // Keep latest entity prefilled when parent provides it for Add Cost Center.
    const prefillsEntity =
      !!costCenter?.entityCode ||
      !!costCenter?.entity ||
      !!costCenter?.entityId;

    if (prefillsEntity) {
      const entityCode = costCenter?.entityCode ?? extractCodeFromLabel(costCenter?.entity);
      const entityLabel = costCenter?.entity ?? costCenter?.entityCode ?? "";
      setSelectedEntity({
        id: costCenter?.entityId ?? 0,
        label: entityLabel,
        value: entityCode,
      });
    } else {
      setSelectedEntity(defaultOption);
    }

    setSelectedArea(defaultOption);
    setSelectedCenter(defaultOption);
    setSelectedLocation(defaultOption);
    setSelectedCostCenter(defaultOption);
    setSelectedShift(defaultOption);
  }, [costCenter]);

  // Handle update mode initialization
  useEffect(() => {
    if (isUpdateCostCenter && costCenter) {
      initializeForUpdate();
    }
  }, [isUpdateCostCenter, costCenter, initializeForUpdate]);

  // Handle add mode initialization
  useEffect(() => {
    if (isAddCostCenter) {
      initializeForAdd();
    }
  }, [isAddCostCenter, initializeForAdd]);

  // Helper function to find matching option by value/label
  const findMatchingOption = (
    options: DropdownOption[],
    searchValue: string,
    useCaseInsensitive: boolean = false
  ): DropdownOption | undefined => {
    if (!searchValue) return undefined;

    return options.find(opt => {
      if (useCaseInsensitive) {
        const optValueLower = opt.value.toLowerCase();
        const optLabelLower = opt.label.toLowerCase();
        const searchLower = searchValue.toLowerCase();
        return optValueLower === searchLower || optLabelLower === searchLower;
      }

      return opt.value === searchValue || opt.label === searchValue;
    });
  };

  // Helper function to sync a single field selection - memoized to prevent recreation
  const syncFieldSelection = React.useCallback((
    options: DropdownOption[],
    value: string | undefined,
    setter: (option: DropdownOption) => void,
    useCaseInsensitive: boolean = false
  ) => {
    if (options.length === 0 || !value) return;

    const matching = findMatchingOption(options, value, useCaseInsensitive);

    if (matching) {
      setter(matching);
    }
  }, []);

  // Helper to clear validation error for a specific field
  const clearValidationError = React.useCallback((field: string) => {
    if (onValidationErrors) {
      const currentErrors = parentValidationErrors || [];
      const remainingErrors = currentErrors.filter(e => e.field !== field);
      onValidationErrors(remainingErrors.length > 0 ? remainingErrors : null);
    }
    setFieldErrors((prev) => { const p = { ...prev }; delete p[field]; return p; });
  }, [onValidationErrors, parentValidationErrors]);

  // Helper to set validation error for a specific field
  const setValidationError = React.useCallback((field: string, message: string, fieldRef: React.RefObject<HTMLDivElement>) => {
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
    const ve: ValidationError[] = [{ field, message, fieldRef }];
    if (onValidationErrors) {
      const currentErrors = parentValidationErrors || [];
      const otherErrors = currentErrors.filter(e => e.field !== field);
      onValidationErrors([...otherErrors, ...ve]);
    }
  }, [onValidationErrors, parentValidationErrors]);

  // Helper to build field errors from validation result
  const buildFieldErrorsFromValidation = React.useCallback((errors: ValidationError[]): Record<string, string> => {
    const newFieldErrors: Record<string, string> = {};
    errors.forEach(err => {
      if (err.field) newFieldErrors[err.field] = err.message;
    });
    return newFieldErrors;
  }, []);

  // Only perform initial sync once when the costCenter is loaded for update mode.
  // Prevents subsequent dropdown option loads from overriding user choices.
  // Re-runs when entity/area options load so we can match against the full option list.
  useEffect(() => {
    if (!isUpdateCostCenter) {
      hasInitializedRef.current = false;
      return;
    }

    if (!costCenter) return;

    // Skip if the user has already manually changed any dropdown
    if (
      entityUserChangedRef.current ||
      areaUserChangedRef.current ||
      centerUserChangedRef.current ||
      locationUserChangedRef.current ||
      costCenterUserChangedRef.current ||
      shiftUserChangedRef.current
    ) return;

    // Only sync fields whose options are available
    if (entityOptions.length > 0) {
      syncFieldSelection(entityOptions, costCenter.entityCode ?? costCenter.entity, setSelectedEntity);
    }
    if (areaOptions.length > 0) {
      syncFieldSelection(areaOptions, costCenter.areaCode ?? costCenter.area, setSelectedArea);
    }
    if (centerOptions.length > 0) {
      syncFieldSelection(centerOptions, costCenter.centerCode ?? costCenter.center, setSelectedCenter);
    }
    if (locationOptions.length > 0) {
      syncFieldSelection(locationOptions, costCenter.locationCode ?? costCenter.location, setSelectedLocation);
    }
    if (formattedCostCenterOptions.length > 0) {
      syncFieldSelection(formattedCostCenterOptions, costCenter.costCenter, setSelectedCostCenter);
    }
    if (shiftOptions.length > 0) {
      syncFieldSelection(shiftOptions, costCenter.shift, setSelectedShift, true);
    }
  }, [costCenter, isUpdateCostCenter, entityOptions, areaOptions, centerOptions, locationOptions, formattedCostCenterOptions, shiftOptions]);


  const refreshDependentOptionsForEntity = React.useCallback((selectedOption: DropdownOption) => {
    if (!regionId) return;

    dispatch(fetchAreaOptions({ regionCode: regionId, sortBy: 'DEPTID' }));

    const areaCode = selectedArea.value;
    if (!areaCode || areaCode === 'Select') return;

    dispatch(fetchCenterOptions({ regionCode: regionId, areaCode, sortBy: 'DEPTID' }));

    const centerCode = selectedCenter.value;
    if (!centerCode || centerCode === 'Select') return;

    dispatch(
      fetchLocationOptions({
        regionCode: regionId,
        areaCode,
        centerCode,
        sortBy: 'DEPTID',
      }),
    );

    const locationCode = selectedLocation.value;
    if (!locationCode || locationCode === 'Select') return;

    dispatch(
      fetchAddEditCostCenterOptions({
        regionCd: regionId,
        areaCd: areaCode,
        centerCd: centerCode,
        locationCd: locationCode,
        entityCd: selectedOption.value === 'Select' ? '' : selectedOption.value,
      }),
    );
  }, [regionId, dispatch, selectedArea.value, selectedCenter.value, selectedLocation.value]);

  const handleSelectedEntityClick = React.useCallback((selectedOption: DropdownOption) => {
    entityUserChangedRef.current = true;
    setSelectedEntity(selectedOption);

    // Always reset Cost Center when Entity changes to avoid stale selection.
    setSelectedCostCenter(defaultOption);

    if (selectedOption?.value && selectedOption.value !== 'Select') {
      clearValidationError('entity');
    } else {
      setValidationError('entity', ERROR_MESSAGES.entity, entityRef);
    }

    refreshDependentOptionsForEntity(selectedOption);
  }, [clearValidationError, setValidationError, refreshDependentOptionsForEntity]);

  // Fetch Center options when Area changes
  const handleSelectedAreaClick = React.useCallback((selectedOption: DropdownOption) => {
    areaUserChangedRef.current = true;
    setSelectedArea(selectedOption);
    
    if (selectedOption?.value && selectedOption.value !== 'Select') {
      clearValidationError('area');
    } else {
      setValidationError('area', ERROR_MESSAGES.area, areaRef);
    }
    
    // Reset dependent dropdowns
    setSelectedCenter(defaultOption);
    setSelectedLocation(defaultOption);
    setSelectedCostCenter(defaultOption);

    // Fetch centerOption based on selected area or reset if "Select" is chosen
    if (selectedOption.value && selectedOption.value !== "Select" && regionId) {
      const areaCode = selectedOption.value ?? String(selectedOption.id);
      dispatch(fetchCenterOptions({ regionCode: regionId, areaCode, sortBy: "DEPTID" }));
    } else {
      // Reset Center, Location, and Cost Center options when Area is set to "Select"
      dispatch(resetCenterOptions());
      dispatch(resetLocationOptions());
      dispatch(resetCostCenterOptions());
    }
  }, [clearValidationError, setValidationError, regionId, dispatch]);

  // Fetch Location options when Center changes
  const handleSelectedCenterClick = React.useCallback((selectedOption: DropdownOption) => {
    centerUserChangedRef.current = true;
    setSelectedCenter(selectedOption);
    
    if (selectedOption?.value && selectedOption.value !== 'Select') {
      clearValidationError('center');
    } else {
      setValidationError('center', ERROR_MESSAGES.center, centerRef);
    }
    
    // Reset dependent dropdowns
    setSelectedLocation(defaultOption);
    setSelectedCostCenter(defaultOption);

    // Fetch location based on selected center or reset if "Select" is chosen
    if (selectedOption.value && selectedOption.value !== "Select" && regionId && selectedArea.value !== "Select") {
      const centerCode = selectedOption.value ?? String(selectedOption.id);
      const areaCode = selectedArea.value ?? String(selectedArea.id);
      dispatch(fetchLocationOptions({ regionCode: regionId, areaCode, centerCode, sortBy: "DEPTID" }));
    } else {
      // Reset Location and Cost Center options when Center is set to "Select"
      dispatch(resetLocationOptions());
      dispatch(resetCostCenterOptions());
    }
  }, [clearValidationError, setValidationError, regionId, selectedArea.value, dispatch]);


  const handleSelectedLocationClick = React.useCallback((selectedOption: DropdownOption) => {
    locationUserChangedRef.current = true;
    setSelectedLocation(selectedOption);
    
    if (selectedOption?.value && selectedOption.value !== 'Select') {
      clearValidationError('location');
    } else {
      setValidationError('location', ERROR_MESSAGES.location, locationRef);
    }
    
    // Reset dependent dropdowns
    setSelectedCostCenter(defaultOption);

    if (selectedOption.value && selectedOption.value !== "Select" && regionId && selectedArea.value !== "Select" && selectedCenter.value !== "Select") {
      const locationCode = selectedOption.value ?? String(selectedOption.id);
      const areaCode = selectedArea.value ?? String(selectedArea.id);
      const centerCode = selectedCenter.value ?? String(selectedCenter.id);
      dispatch(
        fetchAddEditCostCenterOptions({
          regionCd: regionId,
          areaCd: areaCode,
          centerCd: centerCode,
          locationCd: locationCode,
          entityCd: selectedEntity.value === "Select" ? "" : selectedEntity.value,
        }),
      );
    }
  }, [clearValidationError, setValidationError, regionId, selectedArea.value, selectedCenter.value, selectedEntity.value, dispatch]);

  const handleCostCenterClick = React.useCallback((selectedOption: DropdownOption) => {
    costCenterUserChangedRef.current = true;
    setSelectedCostCenter(selectedOption);
    
    if (selectedOption?.value && selectedOption.value !== 'Select') {
      clearValidationError('costCenter');
    } else {
      setValidationError('costCenter', ERROR_MESSAGES.costCenter, costCenterRef);
    }
  }, [clearValidationError, setValidationError]);


  const handleSelectedShiftClick = React.useCallback((selectedOption: DropdownOption) => {
    shiftUserChangedRef.current = true;
    setSelectedShift(selectedOption);
    
    if (selectedOption?.value && selectedOption.value !== 'Select') {
      clearValidationError('shift');
    } else {
      setValidationError('shift', ERROR_MESSAGES.shift, shiftRef);
    }
  }, [clearValidationError, setValidationError]);
  
  // Helper to show success banner and close the add form without clearing the banner
  const showSuccessAndClose = React.useCallback((heading: string) => {
    if (setBanner) {
      setBanner({ heading, messageInfo: "", variant: "Success" });
      setTimeout(() => setBanner(null), 5000);
    }
    if (onCloseKeepBanner) onCloseKeepBanner();
    else onCancel();
  }, [setBanner, onCloseKeepBanner, onCancel]);

  const validateDropdowns = React.useCallback((): { isValid: boolean; errors: ValidationError[] } => {
    const errors: ValidationError[] = [];
    
    const validations = [
      { value: selectedEntity.value, field: 'entity', message: ERROR_MESSAGES.entity, ref: entityRef },
      { value: selectedArea.value, field: 'area', message: ERROR_MESSAGES.area, ref: areaRef },
      { value: selectedCenter.value, field: 'center', message: ERROR_MESSAGES.center, ref: centerRef },
      { value: selectedLocation.value, field: 'location', message: ERROR_MESSAGES.location, ref: locationRef },
      { value: selectedCostCenter.value, field: 'costCenter', message: ERROR_MESSAGES.costCenter, ref: costCenterRef },
      { value: selectedShift.value, field: 'shift', message: ERROR_MESSAGES.shift, ref: shiftRef },
    ];

    validations.forEach(({ value, field, message, ref }) => {
      if (!value || value === "Select") {
        errors.push({ field, message, fieldRef: ref });
      }
    });
    
    return { isValid: errors.length === 0, errors };
  }, [selectedEntity.value, selectedArea.value, selectedCenter.value, selectedLocation.value, selectedCostCenter.value, selectedShift.value]);

  const buildCostCenterData = React.useCallback((): CostCenter => {
    return {
      regionId: (Number(regionId) || costCenter?.regionId) ?? 0,
      glString: glString,
      // Store labels for display in table
      entity: selectedEntity.label,
      area: selectedArea.label,
      center: selectedCenter.label,
      location: selectedLocation.label,
      costCenter: selectedCostCenter.label,
      shift: selectedShift.label,
      // Store codes for API payload
      regionCode: regionId,
      entityCode: selectedEntity.value,
      areaCode: selectedArea.value,
      centerCode: selectedCenter.value,
      locationCode: selectedLocation.value,
      noOfEmployees,
      region: regionId,
      entityId: Number(selectedEntity.id),
      areaId: Number(selectedArea.id),
      centerId: Number(selectedCenter.id),
      locationId: Number(selectedLocation.id),
      costCenterId: Number(selectedCostCenter.id),
      shiftId: Number(selectedShift.id)
    };
  }, [regionId, glString, selectedEntity, selectedArea, selectedCenter, selectedLocation, selectedCostCenter, selectedShift, noOfEmployees, costCenter]);

  // Common function to create UBT team
  const createTeam = React.useCallback((onSuccessCallback: () => void) => {
    const ratingNumber = teamRating ? Number.parseInt(teamRating.toString().split(' ')[0]) : 1;
    const payload = {
      rating: ratingNumber,
      serviceDeliveryType: serviceType,
      teamDateEstablished: convertDateToYYYYMMDD(dateTeamEstablished),
      region: regionId,
      entity: selectedEntity.value,
      area: selectedArea.value,
      location: selectedLocation.value,
      teamName: ubtName,
      costCenter: formatCostCenter(selectedCostCenter.label),
      shift: selectedShift.label,
      usingHuddles: usingHuddles === 'yes' ? 'Y' : 'N',
      defaultMembership: defaultMembership === 'yes' ? 'Y' : 'N',
      center: selectedCenter.value
    };

    setIsCreatingTeam(true);
    dispatch(createUBTTeam(payload))
      .unwrap()
      .then((response) => {
        if (!isMountedRef.current) return;
        const teamId = response.teamId ?? response.id;
        if (teamId) {
          dispatch(fetchUBTTeam({ teamId }))
            .unwrap()
            .then(() => {
              if (!isMountedRef.current) return;
              setFieldErrors({});
              onSuccessCallback();
            })
            .catch(() => {
              if (!isMountedRef.current) return;
              setFieldErrors({});
              onSuccessCallback();
            })
            .finally(() => {
              if (!isMountedRef.current) return;
              setIsCreatingTeam(false);
            });
        } else {
          setFieldErrors({});
          onSuccessCallback();
          setIsCreatingTeam(false);
        }
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        if (setBanner) {
          setBanner({ heading: 'An Error Occurred in Creating this Team', messageInfo: 'We were unable to complete your request. Please try again later.', variant: 'Error' });
        }
        setIsCreatingTeam(false);
      });
  }, [teamRating, serviceType, dateTeamEstablished, regionId, selectedEntity, selectedArea, selectedLocation, ubtName, selectedCostCenter, selectedShift, usingHuddles, defaultMembership, dispatch, setBanner]);

  // Helper for successful create new
  function handleSuccessfulCreateNew() {
    if (onValidationErrors) onValidationErrors([]);
    if (setBanner) {
      setBanner({ heading: "Team created Successfully", messageInfo: "", variant: "Success" });
      setTimeout(() => setBanner(null), 5000);
    }
    // Reset all Redux states
    dispatch(resetTeamProfile());
    dispatch(setCreateTeam(true));
    dispatch(setEditableTeam(false));
    dispatch(setEditingTeam(false));
    // Reset all local dropdown state
    setSelectedEntity(defaultOption);
    setSelectedArea(defaultOption);
    setSelectedCenter(defaultOption);
    setSelectedLocation(defaultOption);
    setSelectedCostCenter(defaultOption);
    setSelectedShift(defaultOption);
    setNoOfEmployees("");
    // Navigate to fresh add-profile page (new location.key triggers reset effect)
    navigate('/home/add-profile', { state: {} });
  }


  // Helper: Build create team payload
  const buildCreateTeamPayload = React.useCallback(() => {
    const ratingNumber = teamRating ? Number.parseInt(teamRating.toString().split(' ')[0]) : 1;
    return {
      rating: ratingNumber,
      serviceDeliveryType: serviceType,
      teamDateEstablished: convertDateToYYYYMMDD(dateTeamEstablished),
      region: regionId,
      entity: selectedEntity.value,
      area: selectedArea.value,
      location: selectedLocation.value,
      teamName: ubtName,
      costCenter: formatCostCenter(selectedCostCenter.label),
      shift: selectedShift.label,
      center: selectedCenter.value,
      usingHuddles: usingHuddles === 'yes' ? 'Y' : 'N',
      defaultMembership: defaultMembership === 'yes' ? 'Y' : 'N'
    };
  }, [teamRating, serviceType, dateTeamEstablished, regionId, selectedEntity, selectedArea, selectedLocation, ubtName, selectedCostCenter, selectedShift, usingHuddles, defaultMembership]);

  // Helper: Show error banner
  const showCreateTeamErrorBanner = React.useCallback(() => {
    if (setBanner) setBanner({ heading: 'An Error Occurred in Creating this Team', messageInfo: 'We were unable to complete your request. Please try again later.', variant: 'Error' });
  }, [setBanner]);

  // Helper: Create team and handle result
  const createTeamAndHandleResult = React.useCallback(async () => {
    setIsCreatingTeam(true);
    try {
      const payload = buildCreateTeamPayload();
      const response = await dispatch(createUBTTeam(payload)).unwrap();
      if (!isMountedRef.current) return;
      const teamId = response.teamId ?? response.id;
      if (teamId) {
        handleSuccessfulCreateNew();
      } else {
        showCreateTeamErrorBanner();
      }
    } catch {
      if (!isMountedRef.current) return;
      showCreateTeamErrorBanner();
    } finally {
      if (isMountedRef.current) setIsCreatingTeam(false);
    }
  }, [dispatch, buildCreateTeamPayload, handleSuccessfulCreateNew, showCreateTeamErrorBanner]);

  const handleSaveAndCreateNew = React.useCallback(async () => {
    const validation = validateDropdowns();
    if (!validation.isValid) {
      if (onValidationErrors) onValidationErrors(validation.errors);
      setFieldErrors(buildFieldErrorsFromValidation(validation.errors));
      if (setBanner) setBanner(null);
      return;
    }

    if (!isCreatingTeamMode && existingTeamId) {
      const costCenterData = buildCostCenterData();
      await _onSaveAndCreateNew(costCenterData);
      return;
    }

    await createTeamAndHandleResult();
  }, [validateDropdowns, isCreatingTeamMode, existingTeamId, buildCostCenterData, _onSaveAndCreateNew, onValidationErrors, setBanner, buildFieldErrorsFromValidation, createTeamAndHandleResult]);

  const handleSaveCostCenter = React.useCallback(async () => {
    const validation = validateDropdowns();
    if (!validation.isValid) {
      if (onValidationErrors) onValidationErrors(validation.errors);
      setFieldErrors(buildFieldErrorsFromValidation(validation.errors));
      if (setBanner) setBanner(null);
      // Removed scroll behavior to keep the banner visible
      return;
    }

    // If we're in edit mode (editing existing team), call the API callback
    if (!isCreatingTeamMode && existingTeamId) {
      const costCenterData = buildCostCenterData();
      const didSave = await _onSave(costCenterData);
      if (didSave) {
        showSuccessAndClose("Cost Center Added Successfully");
      }
    } else {
      // If we're in create mode, create the team first
      if (onValidationErrors) onValidationErrors([]);
      setFieldErrors({});
      createTeam(() => {
        showSuccessAndClose("Team created Successfully");
      });
    }
  }, [
    validateDropdowns,
    isCreatingTeamMode,
    existingTeamId,
    buildCostCenterData,
    _onSave,
    createTeam,
    onCancel,
    onValidationErrors,
    setBanner,
    buildFieldErrorsFromValidation,
  ]);

  // Helper to extract code from label (text before first hyphen or space)
  const extractCodeFromLabel = React.useCallback((value: string | undefined): string => {
    if (!value) return '';
    const hyphenIndex = value.indexOf('-');
    const spaceIndex = value.indexOf(' ');

    if (hyphenIndex > 0 && (spaceIndex < 0 || hyphenIndex < spaceIndex)) {
      return value.substring(0, hyphenIndex).trim();
    }
    if (spaceIndex > 0) {
      return value.substring(0, spaceIndex).trim();
    }
    return value.trim();
  }, []);

  const normalizeComparableValue = React.useCallback(
    (value: string | number | undefined | null): string => String(value ?? '').trim(),
    [],
  );

  const isUpdateDataUnchanged = React.useCallback((): boolean => {
    if (!costCenter) return false;

    const currentEntity =
      selectedEntity.value && selectedEntity.value !== 'Select'
        ? selectedEntity.value
        : selectedEntity.label;
    const currentArea =
      selectedArea.value && selectedArea.value !== 'Select'
        ? selectedArea.value
        : selectedArea.label;
    const currentCenter =
      selectedCenter.value && selectedCenter.value !== 'Select'
        ? selectedCenter.value
        : selectedCenter.label;
    const currentLocation =
      selectedLocation.value && selectedLocation.value !== 'Select'
        ? selectedLocation.value
        : selectedLocation.label;
    const currentCostCenter = formatCostCenter(
      selectedCostCenter.label || selectedCostCenter.value,
    );
    const currentShift = selectedShift.label || selectedShift.value;

    const existingRegion = normalizeComparableValue(
      costCenter.regionCode ?? costCenter.regionId ?? regionId,
    );
    const existingEntityCode =
      costCenter.entityCode ?? extractCodeFromLabel(costCenter.entity);
    const existingAreaCode =
      costCenter.areaCode ?? extractCodeFromLabel(costCenter.area);
    const existingCenterCode =
      costCenter.centerCode ?? extractCodeFromLabel(costCenter.center);
    const existingLocationCode =
      costCenter.locationCode ?? extractCodeFromLabel(costCenter.location);

    const existingEntity = normalizeComparableValue(
      existingEntityCode || costCenter.entity,
    );
    const existingArea = normalizeComparableValue(
      existingAreaCode || costCenter.area,
    );
    const existingCenter = normalizeComparableValue(
      existingCenterCode || costCenter.center,
    );
    const existingLocation = normalizeComparableValue(
      existingLocationCode || costCenter.location,
    );
    const existingCostCenter = formatCostCenter(costCenter.costCenter ?? '');
    const existingShift = normalizeComparableValue(costCenter.shift);

    return (
      normalizeComparableValue(regionId) === existingRegion &&
      normalizeComparableValue(currentEntity) === existingEntity &&
      normalizeComparableValue(currentArea) === existingArea &&
      normalizeComparableValue(currentCenter) === existingCenter &&
      normalizeComparableValue(currentLocation) === existingLocation &&
      currentCostCenter === existingCostCenter &&
      normalizeComparableValue(currentShift) === existingShift
    );
  }, [
    costCenter,
    selectedEntity,
    selectedArea,
    selectedCenter,
    selectedLocation,
    selectedCostCenter,
    selectedShift,
    regionId,
    extractCodeFromLabel,
    normalizeComparableValue,
  ]);

  // Build update cost center payload
  const buildUpdatePayload = React.useCallback(() => {
    return {
      existingCostCenter: formatCostCenter(costCenter?.costCenter ?? ''),
      existingRegion: costCenter?.regionCode ?? regionId,
      existingEntity: costCenter?.entityCode ?? extractCodeFromLabel(costCenter?.entity),
      existingArea: costCenter?.areaCode ?? extractCodeFromLabel(costCenter?.area),
      existingLocation: costCenter?.locationCode ?? extractCodeFromLabel(costCenter?.location),
      existingShift: costCenter?.shift ?? '',
      existingCenter: costCenter?.centerCode ?? extractCodeFromLabel(costCenter?.center),
      newCostCenter: formatCostCenter(selectedCostCenter.label),
      newRegion: regionId,
      newEntity: selectedEntity.value,
      newArea: selectedArea.value,
      newLocation: selectedLocation.value,
      newShift: selectedShift.label,
      newCenter: selectedCenter.value
    };
  }, [costCenter, regionId, selectedEntity, selectedArea, selectedCenter, selectedLocation, selectedCostCenter, selectedShift, extractCodeFromLabel]);

  // Handle update cost center success or error scenarios
  const handleUpdateCostCenterResult = React.useCallback((result: { meta?: { requestStatus?: string } }) => {
    if (result?.meta?.requestStatus === "fulfilled") {
      const updatedCostCenter = buildCostCenterData();
      onUpdate(updatedCostCenter, true);
      if (setBanner) setBanner({ heading: 'Team Updated Successfully', messageInfo: '', variant: 'Success' });
      if (onValidationErrors) onValidationErrors([]);
      setFieldErrors({});
      // Scroll to top after successful update
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (setBanner) {
      setBanner({ heading: 'An Error Occurred in Updating this Cost Center', messageInfo: 'We were unable to complete your request. Please try again later.', variant: 'Error' });
    }
  }, [buildCostCenterData, onUpdate, setBanner, onValidationErrors]);

  const handleUpdateCostCenter = React.useCallback(async () => {
    const validation = validateDropdowns();
    if (!validation.isValid) {
      if (onValidationErrors) onValidationErrors(validation.errors);
      setFieldErrors(buildFieldErrorsFromValidation(validation.errors));
      if (setBanner) setBanner(null);
      // Removed scroll behavior to keep the banner visible
      return;
    }

    if (isUpdateDataUnchanged()) {
      if (onValidationErrors) onValidationErrors([]);
      setFieldErrors({});
      if (setBanner) setBanner(null);
      onUpdate(buildCostCenterData(), false);
      return;
    }

    const payload = buildUpdatePayload();

    try {
      const result: { meta?: { requestStatus?: string } } = await dispatch(
        updateCostCenterAPI({ teamId: existingTeamId, payload })
      );
      handleUpdateCostCenterResult(result);
    } catch {
      if (setBanner) setBanner({ heading: 'An Error Occurred in Updating this Cost Center', messageInfo: 'We were unable to complete your request. Please try again later.', variant: 'Error' });
    }
  }, [validateDropdowns, isUpdateDataUnchanged, onValidationErrors, setBanner, onUpdate, buildCostCenterData, buildUpdatePayload, dispatch, existingTeamId, handleUpdateCostCenterResult, buildFieldErrorsFromValidation]);

  // Handle cancel button click - clear field errors before calling parent onCancel
  const handleCancel = React.useCallback(() => {
    // Clear field errors
    setFieldErrors({});
    // Notify parent to clear its validation errors
    if (onValidationErrors) {
      onValidationErrors(null);
    }
    // Clear banner if available
    if (setBanner) {
      setBanner(null);
    }
    // Call parent's onCancel
    onCancel();
  }, [onCancel, onValidationErrors, setBanner]);

  return (
    <div ref={containerRef}>
      {(isCreatingTeam || loadingCostCenter || loadingDropdowns) && <Loader />}
      <div className="bg-white w-3xl p-8! mt-2!">
        <h2 className="h2 mb-2">{isUpdateCostCenter ? "Update Cost Center" : "Add Cost Center"}</h2>
        <div className="flex gap-x-3 mt-8! mb-0!">
          <div className="min-w-70">
           
              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <div style={{ marginRight: '8px' }}>Region:</div>
                <input
                  className="h4 text-kp-blue!"
                  style={{ width: '100%', minWidth: '200px' }}
                  id="region"
                  name="region"
                  type="text"
                  value={isUpdateCostCenter ? costCenter?.region ?? "" : resolvedRegionDescription ?? profile?.regionDescription ?? ""}
                  disabled
                />
              </div>

            {fieldErrors.region && (
              <ValidationErrorDisplay
                message={fieldErrors.region}
                id="error-region"
              />
            )}
          </div>
        </div>
        <div className="flex w-full! gap-x-50 mt-4! mb-4!">
          <div className="w-1/4!" ref={entityRef}>
            <Dropdown
              label="Entity"
              options={entityOptions}
              selectedValue={selectedEntity}
              onSelect={handleSelectedEntityClick}
              menuClassName="w-full! max-h-65! overflow-y-auto!"
              itemClassName="pr-4!"
              buttonClassName="w-full!"
            />
            {fieldErrors.entity && (
              <ValidationErrorDisplay message={fieldErrors.entity} id="error-entity" />
            )}
          </div>
          <div className="w-1/2!" ref={areaRef}>
            <Dropdown
              label="Area"
              options={filteredAreaOptions}
              selectedValue={selectedArea}
              onSelect={handleSelectedAreaClick}
              menuClassName="min-w-60 w-full! max-h-65! overflow-y-auto!"
              itemClassName="pr-4!"
              buttonClassName="w-full!"
            />
            {fieldErrors.area && (
              <ValidationErrorDisplay message={fieldErrors.area} id="error-area" />
            )}
          </div>
        </div>
        <div className="flex w-full! gap-x-8 mb-4!">
          <div className="w-1/2!" ref={centerRef}>
            <Dropdown
              label="Center"
              options={filteredCenterOptions}
              selectedValue={selectedCenter}
              onSelect={handleSelectedCenterClick}
              menuClassName="w-full! max-h-65! overflow-y-auto!"
              itemClassName="pr-4!"
              buttonClassName="w-full!"
              placeholder="Select"
            />
            {fieldErrors.center && (
              <ValidationErrorDisplay message={fieldErrors.center} id="error-center" />
            )}
          </div>
          <div className="w-1/2!" ref={locationRef}>
            <Dropdown
              label="Location"
              options={filteredLocationOptions}
              selectedValue={selectedLocation}
              onSelect={handleSelectedLocationClick}
              menuClassName="w-full! max-h-65! overflow-y-auto!"
              itemClassName="pr-4!"
              buttonClassName="w-full!"
              placeholder="Select"
            />
            {fieldErrors.location && (
              <ValidationErrorDisplay message={fieldErrors.location} id="error-location" />
            )}
          </div>
        </div>
        <div className="flex w-full! gap-x-8 mb-4!">
          <div className="w-1/2!" ref={costCenterRef}>
            <Dropdown
              label="Cost Center"
              options={formattedCostCenterOptions}
              selectedValue={selectedCostCenter}
              onSelect={handleCostCenterClick}
              menuClassName="w-full! max-h-65! overflow-y-auto!"
              itemClassName="pr-4!"
              buttonClassName="w-full!"
              placeholder="Select"
            />
            {fieldErrors.costCenter && (
              <ValidationErrorDisplay message={fieldErrors.costCenter} id="error-costcenter" />
            )}
          </div>
          <div className="w-1/3!" ref={shiftRef}>
            <Dropdown
              label="Shift"
              options={shiftOptions}
              selectedValue={selectedShift}
              onSelect={handleSelectedShiftClick}
              menuClassName="w-full! max-h-65! overflow-y-auto!"
              itemClassName="pr-4!"
              buttonClassName="w-full!"
            />
            {fieldErrors.shift && (
              <ValidationErrorDisplay message={fieldErrors.shift} id="error-shift" />
            )}
          </div>
        </div>
        <div className="flex gap-x-4 mt-8!">
          {(isUpdateCostCenter || costCenterCount > 0) && (
            <button
              className="button py-0! -inverted hover:text-white!"
              data-analytics-type="button"
              data-analytics-click="Default"
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}
          {isUpdateCostCenter && (
            <button
              className="button py-0! -inverted hover:text-white!"
              data-analytics-type="button"
              data-analytics-click="Default"
              onClick={handleUpdateCostCenter}
            >
              Update and Manage this team
            </button>
          )}
          {isAddCostCenter && (
            <>
              <button
                className="button py-0! -inverted hover:text-white!"
                data-analytics-type="button"
                data-analytics-click="Default"
                onClick={handleSaveCostCenter}
              >
                Save and Manage this team
              </button>
                <button
                  className="button py-0! -inverted hover:text-white!"
                  data-analytics-type="button"
                  data-analytics-click="Default"
                  onClick={async () => await handleSaveAndCreateNew()}
                >
                  Save and Create New
                </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageCostCenter;
